// lib/adparams.js
// Captura y persistencia de los parámetros del aviso (utm_*).
//
// Meta le cuelga sola a cada link sus parámetros dinámicos, y hasta ahora los tirábamos:
//   utm_source  = fb | ig        utm_medium  = paid
//   utm_content = ID DEL AVISO   utm_term = id del adset   utm_campaign = id de campaña
// El link de la bio de Instagram llega distinto (utm_medium=social,
// utm_content=link_in_bio), así que lo orgánico también queda separado.
//
// Sin esto, de una venta solo se sabe "vino de Meta" (por el fbc) pero nunca de QUÉ
// aviso, y todo el ROAS por aviso salía del número que declara Meta, que sobre-reporta.
//
// Calco de fbclid.js y gclid.js: cookie propia a 90 días (la ventana de conversión por
// click) más espejo en localStorage, porque ITP capa a 7 días las cookies escritas por JS.
// Al leer se devuelve además la EDAD EN MINUTOS del click, y esa resta es lo que separa
// "clickeó recién" de "tiene la cookie de hace dos meses". Va la edad y no el timestamp
// porque las dos lecturas salen del mismo reloj: un cliente con la hora mal igual da una
// edad correcta.

import { getCookie } from "@/utils/cookies";

const KEY = "bcm_ad";
const MAX_AGE_DAYS = 90;

/** Los cinco utm que nos interesan, con el largo máximo que acepta el back. */
const CAMPOS = {
  source: 255,
  medium: 255,
  campaign: 500,
  content: 500,
  term: 500,
};

function persist(obj) {
  const raw = JSON.stringify(obj);
  const maxAge = MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${KEY}=${encodeURIComponent(raw)}; max-age=${maxAge}; path=/; SameSite=Lax`;
  try {
    window.localStorage.setItem(KEY, raw);
  } catch {
    /* localStorage puede estar bloqueado (modo privado) */
  }
}

/** Lo guardado, o null si no hay nada legible o venció. */
function leer() {
  if (typeof window === "undefined") return null;

  const fuentes = [];
  const cookie = getCookie(KEY);
  if (cookie) fuentes.push(cookie);
  try {
    const ls = window.localStorage?.getItem(KEY);
    if (ls) fuentes.push(ls);
  } catch {
    /* ignore */
  }

  for (const raw of fuentes) {
    try {
      const obj = JSON.parse(raw);
      if (!obj || typeof obj !== "object") continue;
      if (!Number.isFinite(obj.t) || obj.t <= 0) continue;
      if (Date.now() - obj.t > MAX_AGE_DAYS * 864e5) continue;
      return obj;
    } catch {
      /* valor corrupto: se ignora y se prueba la otra fuente */
    }
  }
  return null;
}

/**
 * Lee los ?utm_* de la URL actual y los persiste.
 * Llamar una vez por pageload (GoogleAdsTag, el mismo punto que fbclid, gclid y wa).
 *
 * Gana el ÚLTIMO click, como en el resto de la atribución de la casa: si la persona
 * vuelve a entrar por otro aviso, ese es el que trajo la venta. Y solo se pisa cuando la
 * URL trae utm de verdad, así que navegar el sitio no borra lo capturado al aterrizar.
 */
export function captureAdParams() {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);

    const obj = { t: Date.now() };
    let hayAlgo = false;
    for (const [campo, largo] of Object.entries(CAMPOS)) {
      const v = params.get(`utm_${campo}`);
      if (v && v.length <= largo) {
        obj[campo] = v;
        hayAlgo = true;
      }
    }
    if (!hayAlgo) return;

    persist(obj);
  } catch {
    /* nunca romper la página por analytics */
  }
}

/**
 * Los utm vigentes para mandar en el checkout, o null si no hay nada.
 * `edadMin` son los minutos desde que se capturó el click.
 */
export function getAdParams() {
  const obj = leer();
  if (!obj) return null;

  const out = {};
  for (const campo of Object.keys(CAMPOS)) {
    if (obj[campo]) out[campo] = obj[campo];
  }
  if (Object.keys(out).length === 0) return null;

  out.edadMin = Math.max(0, Math.round((Date.now() - obj.t) / 60000));
  return out;
}
